import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { BookOpen, FileText, Settings, LogOut, CreditCard, AlertCircle, ClipboardCheck, Users, UserCircle } from 'lucide-react';
import axios from 'axios';
import { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeacherDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [usageInfo, setUsageInfo] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);

  useEffect(() => {
    fetchUsageInfo();
    checkProfile();
  }, []);

  const fetchUsageInfo = async () => {
    try {
      const [settingsRes, subRes] = await Promise.all([
        axios.get(`${API_URL}/api/teacher/settings`),
        axios.get(`${API_URL}/api/subscription/status`)
      ]);
      setUsageInfo({
        settings: settingsRes.data,
        subscription: subRes.data
      });
    } catch (error) {
      console.error('Failed to fetch usage info');
    }
  };

  const checkProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/teacher/profile`);
      setProfile(res.data);
      // Check if profile is complete (has name and school)
      if (!res.data?.name || !res.data?.school_name) {
        setShowProfilePrompt(true);
      }
    } catch (error) {
      // Profile doesn't exist, show prompt
      setShowProfilePrompt(true);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigate = (path) => {
    if (showProfilePrompt && path !== '/profile' && path !== '/settings') {
      return; // Block navigation
    }
    navigate(path);
  };

  const isSubscribed = usageInfo?.subscription?.status === 'active' || 
                       (usageInfo?.subscription?.override_end && new Date(usageInfo.subscription.override_end) > new Date());
  const freePlansUsed = usageInfo?.settings?.free_plans_used || 0;
  const freePlansRemaining = Math.max(0, 5 - freePlansUsed);
  const isProfileComplete = profile?.name && profile?.school_name;

  return (
    <div className="min-h-screen bg-sky-50" data-testid="teacher-dashboard">
      <header className="glass-header py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-600 to-teal-500 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">SINO TECHNOLOGY</h1>
              <p className="text-xs text-slate-600">FOR SCHOOLS</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center text-slate-600 hover:text-slate-900 px-4 py-2 rounded-lg hover:bg-white/50 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-slate-900 mb-2">Welcome Back</h2>
          <p className="text-lg text-slate-600">{profile?.name ? `${profile.name} ${profile.surname || ''}` : user?.email}</p>
        </div>

        {/* Profile Completion Prompt */}
        {showProfilePrompt && (
          <div className="glass-card p-6 mb-6 border-l-4 border-red-500 bg-red-50" data-testid="profile-prompt">
            <div className="flex items-start">
              <UserCircle className="w-6 h-6 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-900 mb-1">Complete Your Profile First</h3>
                <p className="text-red-800">
                  Please complete your profile with your name and school information before accessing other features.
                </p>
                <Button
                  onClick={() => navigate('/profile')}
                  className="mt-3 bg-red-600 hover:bg-red-700 text-white rounded-full"
                >
                  Complete Profile Now
                </Button>
              </div>
            </div>
          </div>
        )}

        {!isSubscribed && usageInfo && !showProfilePrompt && (
          <div className="glass-card p-6 mb-6 border-l-4 border-amber-500" data-testid="usage-warning">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Free Trial</h3>
                <p className="text-slate-700">
                  You have <span className="font-bold text-amber-700">{freePlansRemaining} of 5 free lesson plans</span> remaining. 
                  Subscribe to unlock unlimited generations (20 per subject/month).
                </p>
                <button
                  onClick={() => navigate('/subscription')}
                  className="mt-3 text-sm text-teal-600 hover:text-teal-700 font-medium underline"
                >
                  View Subscription Plans →
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${showProfilePrompt ? 'opacity-50 pointer-events-none' : ''}`}>
          <button
            onClick={() => handleNavigate('/profile')}
            className={`glass-card-hover p-6 text-left group ${!showProfilePrompt ? '' : 'pointer-events-auto opacity-100'}`}
            data-testid="profile-card"
          >
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <UserCircle className="w-6 h-6 text-teal-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">My Profile</h3>
            <p className="text-slate-600">Manage your personal information</p>
            {!isProfileComplete && (
              <span className="mt-2 inline-block px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Incomplete</span>
            )}
          </button>

          <button
            onClick={() => handleNavigate('/settings')}
            className="glass-card-hover p-6 text-left group"
            data-testid="settings-card"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Settings className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Settings</h3>
            <p className="text-slate-600">Configure grades, subjects & resources</p>
          </button>

          <button
            onClick={() => handleNavigate('/lesson-plans')}
            className="glass-card-hover p-6 text-left group"
            data-testid="lesson-plans-card"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Lesson Plans</h3>
            <p className="text-slate-600">Create and manage your lesson plans</p>
          </button>

          <button
            onClick={() => handleNavigate('/assessments')}
            className="glass-card-hover p-6 text-left group"
            data-testid="assessments-card"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <ClipboardCheck className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Assessments</h3>
            <p className="text-slate-600">Create quizzes, worksheets & question banks</p>
          </button>

          <button
            onClick={() => handleNavigate('/learner-profiles')}
            className="glass-card-hover p-6 text-left group"
            data-testid="learner-profiles-card"
          >
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Users className="w-6 h-6 text-pink-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Learner Profiles</h3>
            <p className="text-slate-600">Import marks, profile & support learners</p>
          </button>

          <button
            onClick={() => handleNavigate('/subscription')}
            className="glass-card-hover p-6 text-left group"
            data-testid="subscription-card"
          >
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <CreditCard className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Subscription</h3>
            <p className="text-slate-600">Manage your subscription plan</p>
          </button>
        </div>
      </main>
    </div>
  );
}